import { Component, Inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';

import { DiagnosticProfile } from '../../models/diagnostic-profile';
import { BundleAssemblyService } from '../../services/bundle-assembly.service';
import { DiagnosticBufferService } from '../../services/diagnostic-buffer.service';

interface DiagnosticBundleForm {
  problem: FormControl<string>;
  expected: FormControl<string>;
}

export interface DiagnosticBundleModalData {
  screenshot: Blob | null;
}

@Component({
  selector: 'app-diagnostic-bundle-modal',
  templateUrl: './diagnostic-bundle-modal.component.html',
  styleUrls: ['./diagnostic-bundle-modal.component.scss'],
})
export class DiagnosticBundleModalComponent {
  readonly activeProfile: DiagnosticProfile;
  readonly screenshot: Blob | null;
  submitting = false;
  errorKey: string | null = null;
  includeScreenshot = true;

  form = new FormGroup<DiagnosticBundleForm>({
    problem: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    expected: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
  });

  constructor(
    @Inject(MAT_DIALOG_DATA) data: DiagnosticBundleModalData,
    private readonly bundleAssembly: BundleAssemblyService,
    private readonly dialogRef: MatDialogRef<DiagnosticBundleModalComponent>,
    private readonly diagnosticBuffer: DiagnosticBufferService
  ) {
    this.screenshot = data.screenshot;
    // Snapshot of the active profile at modal-open time. Used only for the
    // read-only "Aktuális részletességi szint" display — submit() re-reads
    // the live profile from the buffer so a user-menu profile change while
    // the modal is open is reflected in manifest.profile (EAS-2040 review).
    this.activeProfile = diagnosticBuffer.getActiveProfile();
  }

  async submit(): Promise<void> {
    if (this.form.invalid || this.submitting) {
      return;
    }
    const { problem, expected } = this.form.getRawValue();
    this.submitting = true;
    this.errorKey = null;
    try {
      // Re-read the live active profile at submit time, not the snapshot
      // taken at modal-open. Per-event profile fields (stamped in
      // DiagnosticBufferService.push) and manifest.profile then agree on
      // the final state of the buffer.
      const liveProfile = this.diagnosticBuffer.getActiveProfile();
      await this.bundleAssembly.assembleAndDownload({
        profile: liveProfile,
        problem: problem.trim(),
        expected: expected.trim(),
        screenshot: this.includeScreenshot ? this.screenshot : null,
      });
      this.dialogRef.close(true);
    } catch (err) {
      console.error('[DiagnosticBundleModal] bundle assembly failed', err);
      this.errorKey = 'DIAGNOSTIC.MODAL.ERROR_RETRY';
    } finally {
      this.submitting = false;
    }
  }

  cancel(): void {
    this.dialogRef.close(false);
  }
}
